import { Button, Card, Chip, Grid, Stack, Typography, ValidationErrors } from "@compass/core";
import { StaffReportServiceSummaryDisplay, StaffReportServiceSummaryDisplayResource } from "generated/openApi";
import produce from "immer";

export const ServiceSummaryDisplay = (props: {
  display: StaffReportServiceSummaryDisplay;
  setDisplay: (filter: StaffReportServiceSummaryDisplay) => void;
  resource: StaffReportServiceSummaryDisplayResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const groupByProperties = new Set(props.display.groupByProperties);
  const aggregateProperties = new Set(props.display.aggregateProperties);

  const groupBy = (
    <Stack spacing="sm">
      <Stack direction="row" spacing="sm" alignItems="center">
        <Typography>Group by fields</Typography>

        {groupByProperties.size !== props.resource.groupableProperties?.length && (
          <Button
            onClick={() =>
              props.setDisplay(
                produce(props.display, (draft) => {
                  draft.groupByProperties = props.resource.groupableProperties?.map((x) => x.propertyName);
                })
              )
            }
            variant="outlined"
            size="small"
          >
            Select All
          </Button>
        )}

        {groupByProperties.size > 0 && (
          <Button
            onClick={() =>
              props.setDisplay(
                produce(props.display, (draft) => {
                  draft.groupByProperties = [];
                })
              )
            }
            variant="outlined"
            size="small"
          >
            Clear
          </Button>
        )}
      </Stack>

      <Grid container spacing="xs">
        {props.resource.groupableProperties?.map((property) => (
          <Grid item>
            <Chip
              label={property.displayName}
              onClick={() =>
                props.setDisplay(
                  produce(props.display, (draft) => {
                    groupByProperties.has(property.propertyName!)
                      ? groupByProperties.delete(property.propertyName!)
                      : groupByProperties.add(property.propertyName!);
                    draft.groupByProperties = Array.from(groupByProperties);
                  })
                )
              }
              variant={groupByProperties.has(property.propertyName!) ? "filled" : "outlined"}
              color={groupByProperties.has(property.propertyName!) ? "success" : "default"}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  const aggregate = (
    <Stack spacing="sm">
      <Stack direction="row" spacing="sm" alignItems="center">
        <Typography>Aggregate fields</Typography>

        {aggregateProperties.size !== props.resource.aggregatableProperties?.length && (
          <Button
            onClick={() =>
              props.setDisplay(
                produce(props.display, (draft) => {
                  draft.aggregateProperties = props.resource.aggregatableProperties?.map((x) => x.propertyName);
                })
              )
            }
            variant="outlined"
            size="small"
          >
            Select All
          </Button>
        )}

        {aggregateProperties.size > 0 && (
          <Button
            onClick={() =>
              props.setDisplay(
                produce(props.display, (draft) => {
                  draft.aggregateProperties = [];
                })
              )
            }
            variant="outlined"
            size="small"
          >
            Clear
          </Button>
        )}
      </Stack>

      <Grid container spacing="xs">
        {props.resource.aggregatableProperties?.map((property) => (
          <Grid item>
            <Chip
              label={property.displayName}
              onClick={() =>
                props.setDisplay(
                  produce(props.display, (draft) => {
                    aggregateProperties.has(property.propertyName!)
                      ? aggregateProperties.delete(property.propertyName!)
                      : aggregateProperties.add(property.propertyName!);
                    draft.aggregateProperties = Array.from(aggregateProperties);
                  })
                )
              }
              variant={aggregateProperties.has(property.propertyName!) ? "filled" : "outlined"}
              color={aggregateProperties.has(property.propertyName!) ? "success" : "default"}
            />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );

  return (
    <Card header={"Display Items"}>
      <Stack spacing="lg">
        {groupBy}

        {aggregate}
      </Stack>
    </Card>
  );
};
